import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Mail, User, MessageSquare, Send, CheckCircle, Loader2, Heart, Eye, Users, Briefcase, Star, Quote, ChevronLeft, ChevronRight } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import Navbar from '../components/common/Navbar';
import Footer from '../components/common/Footer';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const LavoraConNoi = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    nome: '',
    cognome: '',
    email: '',
    messaggio: ''
  });
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [staffReviews, setStaffReviews] = useState([]);
  const [currentReviewIndex, setCurrentReviewIndex] = useState(0);

  useEffect(() => {
    window.scrollTo(0, 0);
    loadStaffReviews();
  }, []);

  // Auto-rotate reviews
  useEffect(() => {
    if (staffReviews.length <= 1) return;
    const interval = setInterval(() => {
      setCurrentReviewIndex(prev => (prev + 1) % staffReviews.length);
    }, 6000);
    return () => clearInterval(interval);
  }, [staffReviews.length]);

  const loadStaffReviews = async () => {
    try {
      const response = await axios.get(`${API}/reviews/staff`);
      if (response.data && response.data.length > 0) {
        setStaffReviews(response.data);
      } else {
        // Fallback quando non ci sono recensioni
        setStaffReviews([
          { 
            user_initials: "M.B.", 
            user_role: "doctor", 
            rating: 5, 
            title: "Un ambiente stimolante",
            content: "Lavorare qui mi ha permesso di crescere professionalmente in un ambiente all'avanguardia."
          },
          {
            user_initials: "S.R.",
            user_role: "ortottista",
            rating: 5,
            title: "Team fantastico",
            content: "Il team è collaborativo e professionale. Ogni giorno è un'opportunità di crescita."
          }
        ]);
      }
    } catch (error) {
      console.error('Error loading staff reviews:', error);
      // Fallback reviews
      setStaffReviews([
        { 
          user_initials: "M.B.", 
          user_role: "doctor", 
          rating: 5, 
          title: "Un ambiente stimolante",
          content: "Lavorare qui mi ha permesso di crescere professionalmente in un ambiente all'avanguardia."
        },
        {
          user_initials: "S.R.",
          user_role: "ortottista",
          rating: 5,
          title: "Team fantastico",
          content: "Il team è collaborativo e professionale. Ogni giorno è un'opportunità di crescita."
        }
      ]);
    }
  };

  const getRoleLabel = (role) => {
    switch(role) {
      case 'doctor': return 'Medico Oculista';
      case 'ortottista': return 'Ortottista';
      case 'admin': return 'Staff Amministrativo';
      default: return 'Team PRIME VISION';
    }
  };

  const nextReview = () => {
    setCurrentReviewIndex(prev => (prev + 1) % staffReviews.length);
  };

  const prevReview = () => {
    setCurrentReviewIndex(prev => (prev - 1 + staffReviews.length) % staffReviews.length);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.nome || !formData.cognome || !formData.email) {
      toast.error('Compila tutti i campi obbligatori');
      return;
    }
    
    setLoading(true);

    try {
      await axios.post(`${BACKEND_URL}/api/careers/apply`, formData);
      setSubmitted(true);
      toast.success('Candidatura inviata con successo!');
    } catch (error) {
      console.error('Error:', error);
      toast.error('Errore durante l\'invio. Riprova.');
    } finally {
      setLoading(false);
    }
  };

  if (submitted) {
    return (
      <div className="min-h-screen" style={{ background: 'linear-gradient(160deg, #eaf5fc 0%, #f7fbfe 50%, #e1eef8 100%)' }}>
        <Navbar />
        <div className="pt-32 pb-20 px-4">
          <div className="max-w-2xl mx-auto text-center">
            <div className="w-24 h-24 rounded-full bg-green-100 flex items-center justify-center mx-auto mb-6">
              <CheckCircle className="w-12 h-12 text-green-600" />
            </div>
            <h1 className="text-3xl font-bold mb-4" style={{ color: '#2A9FCB', fontFamily: "'Lora', Georgia, serif" }}>
              Grazie per averci contattato!
            </h1>
            <p className="text-lg mb-4" style={{ color: '#2A9FCB' }}>
              Abbiamo ricevuto i tuoi dati e ti abbiamo inviato un'email di conferma.
            </p>
            <p className="text-base mb-8" style={{ color: '#666' }}>
              Il nostro team valuterà la tua candidatura e ti ricontatterà al più presto.
            </p>
            <button 
              onClick={() => navigate('/')}
              className="px-8 py-3 rounded-xl text-white font-medium"
              style={{ background: 'linear-gradient(135deg, #2A9FCB 0%, #2A9FCB 100%)' }}
            >
              Torna alla Home
            </button>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen" style={{ background: 'linear-gradient(160deg, #eaf5fc 0%, #f7fbfe 50%, #e1eef8 100%)' }}>
      <Navbar />
      
      {/* Hero Section */}
      <section className="pt-32 pb-16 px-4 relative overflow-hidden" style={{background: 'linear-gradient(135deg, #2A9FCB 0%, #2A9FCB 100%)'}}>
        <div className="max-w-4xl mx-auto text-center relative z-10">
          <div className="inline-flex items-center justify-center w-20 h-20 rounded-full mb-6" style={{background: 'rgba(255,255,255,0.2)'}}>
            <Briefcase className="w-10 h-10 text-white" />
          </div>
          <h1 className="text-4xl sm:text-5xl font-bold text-white mb-4" style={{ fontFamily: "'Lora', Georgia, serif" }}>
            Lavora con Noi
          </h1>
          <p className="text-xl text-white/90" style={{ fontFamily: "'Lora', Georgia, serif" }}>
            Unisciti al team Prime Vision
          </p>
        </div>
        
        {/* Decorative elements */}
        <div className="absolute top-20 right-10 w-64 h-64 rounded-full opacity-10" style={{background: 'radial-gradient(circle, white 0%, transparent 70%)'}}></div>
        <div className="absolute bottom-10 left-10 w-48 h-48 rounded-full opacity-10" style={{background: 'radial-gradient(circle, white 0%, transparent 70%)'}}></div>
      </section>

      {/* Main Content */}
      <section className="py-16 px-4">
        <div className="max-w-4xl mx-auto">
          
          {/* Why Work With Us */}
          <div className="grid md:grid-cols-3 gap-6 mb-16">
            <div className="bg-white rounded-2xl p-6 shadow-sm text-center">
              <div className="w-14 h-14 rounded-full mx-auto mb-4 flex items-center justify-center" style={{background: 'rgba(127, 179, 160, 0.2)'}}>
                <Heart className="w-7 h-7" style={{color: '#2A9FCB'}} />
              </div>
              <h3 className="font-bold mb-2" style={{color: '#2A9FCB'}}>Prevenzione al Centro</h3>
              <p className="text-sm text-gray-600">Crediamo che la vera cura inizi prima della malattia</p>
            </div>
            
            <div className="bg-white rounded-2xl p-6 shadow-sm text-center">
              <div className="w-14 h-14 rounded-full mx-auto mb-4 flex items-center justify-center" style={{background: 'rgba(127, 179, 160, 0.2)'}}>
                <Eye className="w-7 h-7" style={{color: '#2A9FCB'}} />
              </div>
              <h3 className="font-bold mb-2" style={{color: '#2A9FCB'}}>Tecnologia Avanzata</h3>
              <p className="text-sm text-gray-600">Strumentazione all'avanguardia per diagnosi precise</p>
            </div>
            
            <div className="bg-white rounded-2xl p-6 shadow-sm text-center">
              <div className="w-14 h-14 rounded-full mx-auto mb-4 flex items-center justify-center" style={{background: 'rgba(127, 179, 160, 0.2)'}}>
                <Users className="w-7 h-7" style={{color: '#2A9FCB'}} />
              </div>
              <h3 className="font-bold mb-2" style={{color: '#2A9FCB'}}>Team Affiatato</h3>
              <p className="text-sm text-gray-600">Ambiente di lavoro stimolante e professionale</p>
            </div>
          </div>

          {/* Staff Reviews Section */}
          {staffReviews.length > 0 && (
            <div className="mb-16">
              <div className="text-center mb-8">
                <h2 className="text-2xl font-bold mb-3" style={{ color: '#2A9FCB', fontFamily: "'Lora', Georgia, serif" }}>
                  Cosa dice chi lavora con noi
                </h2>
                <p className="text-gray-600">
                  Le testimonianze del nostro team
                </p>
              </div>

              <div className="bg-white rounded-2xl p-8 shadow-lg relative">
                <Quote className="absolute top-6 left-6 w-10 h-10 opacity-10" style={{color: '#2A9FCB'}} />
                
                <div className="text-center">
                  {/* Stars */}
                  <div className="flex justify-center gap-1 mb-4">
                    {[1,2,3,4,5].map(i => (
                      <Star 
                        key={i} 
                        className={`w-5 h-5 ${i <= (staffReviews[currentReviewIndex]?.rating || 5) ? 'fill-current' : ''}`}
                        style={{color: '#D4AF37'}}
                      />
                    ))}
                  </div>
                  
                  {/* Review Title */}
                  {staffReviews[currentReviewIndex]?.title && (
                    <h3 className="font-semibold text-lg mb-2" style={{color: '#2A9FCB'}}>
                      {staffReviews[currentReviewIndex]?.title}
                    </h3>
                  )}
                  
                  {/* Review Text */}
                  <p className="text-lg italic mb-6" style={{color: '#2A9FCB'}}>
                    "{staffReviews[currentReviewIndex]?.content}"
                  </p>
                  
                  {/* Reviewer Info */}
                  <div className="mb-6">
                    <p className="font-semibold" style={{color: '#2A9FCB'}}>
                      {staffReviews[currentReviewIndex]?.user_initials}
                    </p>
                    <p className="text-sm" style={{color: '#9CA3AF'}}>
                      {getRoleLabel(staffReviews[currentReviewIndex]?.user_role)}
                    </p>
                  </div>

                  {/* Navigation */}
                  {staffReviews.length > 1 && (
                    <div className="flex items-center justify-center gap-4">
                      <button 
                        onClick={prevReview}
                        className="p-2 rounded-full hover:bg-gray-100 transition-colors"
                        style={{color: '#2A9FCB'}}
                      >
                        <ChevronLeft className="w-6 h-6" />
                      </button>
                      
                      {/* Dots */}
                      <div className="flex gap-2">
                        {staffReviews.map((_, idx) => (
                          <button
                            key={idx}
                            onClick={() => setCurrentReviewIndex(idx)}
                            className={`w-2 h-2 rounded-full transition-all ${
                              idx === currentReviewIndex ? 'w-6' : ''
                            }`}
                            style={{
                              background: idx === currentReviewIndex ? '#2A9FCB' : '#E5E7EB'
                            }}
                          />
                        ))}
                      </div>
                      
                      <button 
                        onClick={nextReview}
                        className="p-2 rounded-full hover:bg-gray-100 transition-colors"
                        style={{color: '#2A9FCB'}}
                      >
                        <ChevronRight className="w-6 h-6" />
                      </button>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Form Section */}
          <div className="bg-white rounded-3xl shadow-lg overflow-hidden">
            <div className="p-8 sm:p-12">
              <div className="text-center mb-8">
                <h2 className="text-2xl font-bold mb-3" style={{ color: '#2A9FCB', fontFamily: "'Lora', Georgia, serif" }}>
                  Lascia qui i tuoi contatti
                </h2>
                <p className="text-gray-600">
                  per essere ricontattato da noi quanto prima!
                </p>
              </div>

              <form onSubmit={handleSubmit} className="max-w-lg mx-auto space-y-5">
                {/* Nome */}
                <div>
                  <label className="block text-sm font-medium mb-2" style={{color: '#2A9FCB'}}>
                    Nome *
                  </label>
                  <div className="relative">
                    <User className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5" style={{color: '#2A9FCB'}} />
                    <input
                      type="text"
                      value={formData.nome}
                      onChange={(e) => setFormData({...formData, nome: e.target.value})}
                      className="w-full pl-12 pr-4 py-3 border rounded-xl focus:outline-none focus:ring-2"
                      style={{borderColor: '#E5E7EB'}}
                      placeholder="Il tuo nome"
                      required
                    />
                  </div>
                </div>

                {/* Cognome */}
                <div>
                  <label className="block text-sm font-medium mb-2" style={{color: '#2A9FCB'}}>
                    Cognome *
                  </label>
                  <div className="relative">
                    <User className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5" style={{color: '#2A9FCB'}} />
                    <input
                      type="text"
                      value={formData.cognome}
                      onChange={(e) => setFormData({...formData, cognome: e.target.value})}
                      className="w-full pl-12 pr-4 py-3 border rounded-xl focus:outline-none focus:ring-2"
                      style={{borderColor: '#E5E7EB'}}
                      placeholder="Il tuo cognome"
                      required
                    />
                  </div>
                </div>

                {/* Email */}
                <div>
                  <label className="block text-sm font-medium mb-2" style={{color: '#2A9FCB'}}>
                    Email *
                  </label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5" style={{color: '#2A9FCB'}} />
                    <input
                      type="email"
                      value={formData.email}
                      onChange={(e) => setFormData({...formData, email: e.target.value})}
                      className="w-full pl-12 pr-4 py-3 border rounded-xl focus:outline-none focus:ring-2"
                      style={{borderColor: '#E5E7EB'}}
                      placeholder="La tua email"
                      required
                    />
                  </div>
                </div>

                {/* Messaggio */}
                <div>
                  <label className="block text-sm font-medium mb-2" style={{color: '#2A9FCB'}}>
                    Messaggio
                  </label>
                  <div className="relative">
                    <MessageSquare className="absolute left-3 top-4 w-5 h-5" style={{color: '#2A9FCB'}} />
                    <textarea
                      value={formData.messaggio}
                      onChange={(e) => setFormData({...formData, messaggio: e.target.value})}
                      className="w-full pl-12 pr-4 py-3 border rounded-xl focus:outline-none focus:ring-2 min-h-[120px]"
                      style={{borderColor: '#E5E7EB'}}
                      placeholder="Raccontaci di te, le tue esperienze e perché vorresti lavorare con noi..."
                    />
                  </div>
                </div>

                {/* Submit */}
                <button
                  type="submit"
                  disabled={loading}
                  className="w-full py-4 rounded-xl text-white font-semibold flex items-center justify-center gap-2 transition-all hover:opacity-90"
                  style={{
                    background: loading 
                      ? '#9EC5B5' 
                      : 'linear-gradient(135deg, #2A9FCB 0%, #2A9FCB 100%)'
                  }}
                >
                  {loading ? (
                    <>
                      <Loader2 className="w-5 h-5 animate-spin" />
                      Invio in corso...
                    </>
                  ) : (
                    <>
                      <Send className="w-5 h-5" />
                      Invia Candidatura
                    </>
                  )}
                </button>

                <p className="text-xs text-center text-gray-500 mt-4">
                  Inviando questo form acconsenti al trattamento dei tuoi dati personali secondo la nostra{' '}
                  <a href="/privacy" className="underline" style={{color: '#2A9FCB'}}>Privacy Policy</a>
                </p>
              </form>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default LavoraConNoi;
